import { computed, inject, Injector, signal } from "@angular/core";
import { CommonForm, CommonOptionsFieldConfig, CommonSelectField, CommonTextField } from "@eforall/common";
import { DescriptiveSelectItem } from "@eforall/dashboard";
import { DomainDataService } from "../../../../services";
import { DropoutDialogDataService } from "./dropout-dialog-data.service";

export interface DropoutFormFields {
	dropoutWeek: string,
	dropoutReasonId: number,
	notes: string,
}

export type DropoutTargetType = 'Venture' | 'Mentor';


export class DropoutDialogForm {

	public readonly form = new CommonForm<DropoutFormFields>(inject(Injector));

	public dialogDataService = inject(DropoutDialogDataService);
	private readonly domainDataService = inject(DomainDataService);
	private dropoutTargetType: DropoutTargetType | undefined;

	constructor(dropoutTargetType: DropoutTargetType | undefined) {
		this.dropoutTargetType = dropoutTargetType;
	}

	// changing the value to string , as the default value of select field<number> is 0
	// which is causing issue if the actual value is 0
	private getDropoutWeekOptions = computed(() => {
		const weeks = [];
		for (let i = 0; i <= 12; i++) {
			weeks.push({ value: `${i}`, text: `Week ${i}` });
		}

		return weeks;
	});
	readonly dropOutWeek = computed<CommonSelectField<string | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<string>>({
			label: 'Drop-out Week',
			isRequired: true,
			options: this.getDropoutWeekOptions(),
		}),
		formFieldName: 'dropoutWeek',
		form: this.form,
		actualValue: computed(() => this.dialogDataService.dialogData()?.dropoutWeek?.toString()),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
	}));


	private readonly dropoutReasonOptions = computed(() => {
		return this.domainDataService.data().dropoutReasons
			.filter(a => {
				if (!a.isActive) return false;
				if (this.dropoutTargetType === 'Venture') return a.isVentureReason;
				if (this.dropoutTargetType === 'Mentor') return a.isMentorReason;
				return true;
			})
			.map(v => ({
				value: v.dropoutReasonId,
				text: v.name,
				subText: v.desc
			}));
	});

	readonly dropoutReason = computed<CommonSelectField<number | undefined>>(() => ({
		config: signal<CommonOptionsFieldConfig<number>>({
			label: 'Drop-out Reason',
			options: this.dropoutReasonOptions(),
			isRequired: true,
			isSpanish: false
		}),
		actualValue: computed(() => this.dialogDataService.dialogData()?.dropoutReasonId),
		pendingValue: signal<number | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'dropoutReasonId',
		form: this.form,
	}));
	/**
	 * Convert status options to DescriptiveSelectItem format for the dashboard widget
	 */
	public readonly descriptiveStatusItems = computed<DescriptiveSelectItem[]>(() => {
		const options = this.dropoutReasonOptions();

		return options.map(option => {

			return {
				uniqueId: option.value,
				text: option.text,
				subText: option.subText,
			};
		});
	});

	/**
	 * Get the selected descriptive item for the dashboard widget
	 */
	public readonly selectedDescriptiveItem = computed<DescriptiveSelectItem | undefined>(() => {
		const currentValue = this.dropoutReason().pendingValue() || this.dropoutReason().actualValue();
		return this.descriptiveStatusItems().find(item => item.uniqueId === currentValue);
	});

	/**
	 * Handle selection change from the descriptive select field
	 */
	public onDescriptiveSelectionChange(item: DescriptiveSelectItem | undefined) {
		if (item?.uniqueId !== undefined && typeof item.uniqueId === 'number') {
			// Update the pending value - this will sync with the hidden common-select-field
			this.dropoutReason().pendingValue.set(item.uniqueId);
		}
	}


	readonly note = computed<CommonTextField>(() => ({
		config: signal({
			label: 'Drop-out Notes',
			min: 20,
			fixCase: 'sentence-case',
			isRequired: true,
			multiLine: true,
			isSpanish: false
		}),
		actualValue: computed(() => this.dialogDataService.dialogData()?.notes ?? ''),
		pendingValue: signal<string | null>(null),
		saving: signal(false),
		error: signal(''),
		formFieldName: 'notes',
		form: this.form,
	}));
}